import { useCallback, useEffect, useState } from 'react';
import api from '../services/api.js';

const KINDS = ['Near miss', 'Incident', 'Accident', 'Illness', 'Non-conformance'];
const localNow = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
const when = (ts) =>
  new Date(ts).toLocaleString('en-US', { timeZone: 'Asia/Manila', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const blank = () => ({ tugId: '', kind: 'Near miss', occurredAt: localNow(), particulars: '', lostTime: false, ncparNo: '' });

/**
 * Report a safety incident and see recent ones. On a crew page pass `fixedTug`
 * ({ id, name }) and the tug is not asked for; the office picks from all tugs.
 */
export default function SafetyIncidents({ fixedTug = null }) {
  const [rows, setRows] = useState([]);
  const [tugs, setTugs] = useState([]);
  const [form, setForm] = useState(blank);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await api.listSafetyIncidents());
    } catch (e) {
      setMsg({ type: 'danger', text: e.message || 'Failed to load incidents.' });
    }
  }, []);
  useEffect(() => {
    load();
    if (!fixedTug) api.getDtrTugs().then((t) => setTugs(t || [])).catch(() => setTugs([]));
  }, [load, fixedTug]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    const tugId = fixedTug?.id || form.tugId;
    if (!tugId || !form.particulars.trim() || !form.occurredAt) {
      setMsg({ type: 'danger', text: 'Tug, time and what happened are required.' });
      return;
    }
    setSaving(true);
    try {
      await api.reportSafetyIncident({
        tugId,
        kind: form.kind,
        occurredAt: new Date(form.occurredAt).toISOString(),
        particulars: form.particulars.trim(),
        lostTime: form.lostTime,
        ncparNo: form.ncparNo.trim() || null,
      });
      setMsg({ type: 'success', text: `${form.kind} reported.` });
      setForm({ ...blank(), tugId: form.tugId });
      await load();
    } catch (err) {
      setMsg({ type: 'danger', text: err.details?.[0]?.message || err.message || 'Failed to report the incident.' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setMsg(null);
    try {
      await api.deleteSafetyIncident(id);
      await load();
    } catch (err) {
      setMsg({ type: 'danger', text: err.message || 'Failed to remove the incident.' });
    }
  };

  const shown = fixedTug ? rows.filter((r) => r.tugName === fixedTug.name) : rows;

  return (
    <div className="card border-0 shadow-sm" data-testid="safety-incidents">
      <div className="card-header bg-white pt-3 border-bottom-0">
        <h6 className="mb-0 fw-bold text-uppercase small text-secondary">
          Safety incidents{fixedTug ? ` — ${fixedTug.name}` : ''}
        </h6>
      </div>
      <div className="card-body">
        {msg && <div className={`alert alert-${msg.type} py-2`} role="alert" data-testid="incident-message">{msg.text}</div>}
        <form className="row g-2 align-items-end mb-3" onSubmit={submit} noValidate>
          {!fixedTug && (
            <div className="col-md-2">
              <label htmlFor="inc-tug" className="form-label small fw-bold text-muted">Tug</label>
              <select id="inc-tug" className="form-select form-select-sm" value={form.tugId} onChange={(e) => setForm({ ...form, tugId: e.target.value })} data-testid="incident-tug">
                <option value="">Select…</option>
                {tugs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div className="col-md-2">
            <label htmlFor="inc-kind" className="form-label small fw-bold text-muted">Type</label>
            <select id="inc-kind" className="form-select form-select-sm" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="incident-kind">
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label htmlFor="inc-time" className="form-label small fw-bold text-muted">Date &amp; time</label>
            <input id="inc-time" type="datetime-local" className="form-control form-control-sm" value={form.occurredAt} onChange={(e) => setForm({ ...form, occurredAt: e.target.value })} data-testid="incident-time" />
          </div>
          <div className={fixedTug ? 'col-md-4' : 'col-md-3'}>
            <label htmlFor="inc-what" className="form-label small fw-bold text-muted">What happened</label>
            <input id="inc-what" className="form-control form-control-sm" maxLength={4000} value={form.particulars} onChange={(e) => setForm({ ...form, particulars: e.target.value })} data-testid="incident-particulars" />
          </div>
          <div className="col-md-2">
            <label htmlFor="inc-ncpar" className="form-label small fw-bold text-muted">NCPAR # (if any)</label>
            <input id="inc-ncpar" className="form-control form-control-sm" maxLength={60} value={form.ncparNo} onChange={(e) => setForm({ ...form, ncparNo: e.target.value })} data-testid="incident-ncpar" />
          </div>
          <div className="col-md-1">
            <button type="submit" className="btn btn-primary btn-sm w-100" disabled={saving} data-testid="incident-save">Report</button>
          </div>
          <div className="col-12">
            <div className="form-check">
              <input id="inc-lost" type="checkbox" className="form-check-input" checked={form.lostTime} onChange={(e) => setForm({ ...form, lostTime: e.target.checked })} data-testid="incident-lost-time" />
              <label htmlFor="inc-lost" className="form-check-label small">
                Lost-time accident (someone could not work their next shift) — this restarts the safe man-hours count
              </label>
            </div>
          </div>
        </form>
        {shown.length === 0 ? (
          <p className="text-muted small mb-0" data-testid="no-incidents">No incidents reported in the last 30 days.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle small mb-0">
              <thead><tr><th>When</th><th>Tug</th><th>Type</th><th>What happened</th><th>NCPAR #</th><th>Reported by</th><th /></tr></thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} data-testid="incident-row" data-kind={r.kind}>
                    <td>{when(r.occurredAt)}</td>
                    <td className="fw-bold">{r.tugName}</td>
                    <td>{r.kind}{r.lostTime ? <span className="badge bg-danger ms-1">Lost time</span> : null}</td>
                    <td>{r.particulars}</td>
                    <td>{r.ncparNo || '—'}</td>
                    <td className="text-muted">{r.reportedBy || '—'}</td>
                    <td className="text-end">
                      {r.removable && (
                        <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => remove(r.id)} data-testid="incident-remove">Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
